/**
 * AI agent runs and usage — read hooks.
 *
 *   useAgentRuns(id)         → newest-first infinite list, keyset cursor
 *   useAgentRun(id, runId)   → ONE run with its tool-call steps
 *   useAgentUsage(id)        → today's tokens and the last hour's turns vs. the limits
 *
 * All three are readable by `manage-agents` holders and by the agent's
 * accountable person; anyone else gets a 404, which callers render as the
 * generic load-failed state. Every key sits under the agent's own detail key
 * so one prefix invalidation (`agentRunsRootKey`) covers a finished turn.
 */

import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import type { QueryClient } from '@tanstack/react-query'

import { apiClient } from '@/api/client'
import type { AgentRunDetail, AgentRunsPage, AgentUsage } from '@/api/types-bridge'

import { agentQueryKey } from './use-agents'

const PAGE_SIZE = 25

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * The agent id arrives from the `?agentRuns=` URL param and is interpolated
 * into an API path, so anything that isn't a UUID must never reach the
 * network.
 */
export const isAgentUuid = (value: string | null | undefined): value is string =>
  typeof value === 'string' && UUID_RE.test(value)

export const agentRunsQueryKey = (agentId: string) => [...agentQueryKey(agentId), 'runs'] as const
export const agentRunQueryKey = (agentId: string, runId: string) =>
  [...agentRunsQueryKey(agentId), runId] as const
export const agentUsageQueryKey = (agentId: string) => [...agentQueryKey(agentId), 'usage'] as const

/**
 * A turn started or finished: the runs list, any open run's steps and the
 * usage meter are all stale. Prefix invalidation, so unmounted caches are
 * only marked stale, not refetched.
 */
export const invalidateAgentRunSurfaces = (queryClient: QueryClient, agentId: string) => {
  queryClient.invalidateQueries({ queryKey: agentRunsQueryKey(agentId) })
  queryClient.invalidateQueries({ queryKey: agentUsageQueryKey(agentId) })
}

export function useAgentRuns(agentId: string | null, { enabled = true } = {}) {
  return useInfiniteQuery({
    queryKey: agentRunsQueryKey(agentId ?? ''),
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE) })
      if (pageParam) params.set('cursor', pageParam)
      return apiClient<AgentRunsPage>(`/api/agents/${agentId}/runs?${params.toString()}`)
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: enabled && isAgentUuid(agentId),
    staleTime: 15_000,
    refetchOnWindowFocus: false,
    retry: false,
  })
}

export function useAgentRun(agentId: string | null, runId: string | null, { enabled = true } = {}) {
  return useQuery({
    queryKey: agentRunQueryKey(agentId ?? '', runId ?? ''),
    queryFn: async () => {
      const res = await apiClient<{ data: AgentRunDetail }>(`/api/agents/${agentId}/runs/${runId}`)
      return res.data
    },
    enabled: enabled && isAgentUuid(agentId) && isAgentUuid(runId),
    staleTime: 15_000,
    refetchOnWindowFocus: false,
    retry: false,
  })
}

export function useAgentUsage(agentId: string | null, { enabled = true } = {}) {
  return useQuery({
    queryKey: agentUsageQueryKey(agentId ?? ''),
    queryFn: async () => {
      const res = await apiClient<{ data: AgentUsage }>(`/api/agents/${agentId}/usage`)
      return res.data
    },
    enabled: enabled && isAgentUuid(agentId),
    staleTime: 15_000,
    refetchOnWindowFocus: false,
    retry: false,
  })
}
